import { useEffect, useRef, useState } from 'react'
import s from './CopyButton.module.scss'

/** Wie lange die Rückmeldung nach dem Kopieren stehen bleibt. */
const FEEDBACK_MS = 1400

/**
 * Kopiert einen Zellenwert in die Zwischenablage.
 *
 * Zwei Dinge, die leicht übersehen werden und den Knopf sonst unbrauchbar
 * machen:
 *
 *   1. Der Klick darf nicht bis zur Zeile durchreichen. Eine Zeile kann
 *      anklickbar sein — wer kopieren will, landete sonst auf der Detailseite.
 *   2. Es braucht eine Rückmeldung. Die Zwischenablage ist unsichtbar; ohne
 *      Häkchen weiß niemand, ob der Klick gewirkt hat.
 *
 * `navigator.clipboard` gibt es nur in einem sicheren Zusammenhang — über
 * HTTPS oder auf localhost. Wird die Anwendung später über eine nackte
 * IP-Adresse ausgeliefert, schlägt das Kopieren fehl; deshalb der sichtbare
 * Fehlerfall statt einer stummen Wirkungslosigkeit.
 */
export function CopyButton({ value, label }: {
  value: string
  /** Was kopiert wird, für die Beschriftung: "ISIN kopieren". */
  label: string
}) {
  const [state, setState] = useState<'idle' | 'done' | 'failed'>('idle')
  const timer = useRef<number | undefined>(undefined)

  // Läuft die Rückmeldung noch, wenn die Zeile verschwindet — beim Blättern
  // etwa —, würde der Timer auf eine abgeräumte Komponente schreiben.
  useEffect(() => () => window.clearTimeout(timer.current), [])

  async function copy(event: React.MouseEvent) {
    event.stopPropagation()
    event.preventDefault()
    try {
      await navigator.clipboard.writeText(value)
      setState('done')
    } catch {
      setState('failed')
    }
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => setState('idle'), FEEDBACK_MS)
  }

  return (
    <button
      type="button"
      className={s.button}
      data-done={state === 'done'}
      data-failed={state === 'failed'}
      title={state === 'done' ? 'kopiert'
           : state === 'failed' ? 'Kopieren nicht möglich'
           : `${label} kopieren`}
      aria-label={`${label} kopieren`}
      onClick={copy}
    >
      {state === 'done' ? <CheckIcon /> : <CopyIcon />}
    </button>
  )
}

/*
  Die Zeichen als Pfade im Quelltext und nicht aus einer Bibliothek: Zwei
  Symbole rechtfertigen keine zusätzliche Abhängigkeit, und ein Zeichensatz
  müsste geladen werden, bevor die Tabelle vollständig aussieht.
*/

function CopyIcon() {
  return (
    <svg className={s.icon} viewBox="0 0 14 14" fill="none"
         stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
      <rect x="4.5" y="4.5" width="8" height="8" rx="1.5" />
      <path d="M9.5 2.5v-.5a1 1 0 0 0-1-1H2.5a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1H2" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg className={s.icon} viewBox="0 0 14 14" fill="none"
         stroke="currentColor" strokeWidth="2" strokeLinecap="round"
         strokeLinejoin="round" aria-hidden="true">
      <path d="M2.5 7.5l3 3 6-7" />
    </svg>
  )
}
